import type { SocialProfile } from '../SelectSocialProfileOption';
import type { PortfolioMarkers } from '../SelectSocialProfiles';

// The key of the column of pages outside every portfolio. Cannot collide with
// a Meta business id. Same value as the one-list picker's private constant.
export const OUTSIDE_GROUP_KEY = 'outside-portfolios';

export type PortfolioColumn = {
  // The portfolio's Meta business id, or `OUTSIDE_GROUP_KEY`.
  key: string;
  // `name || id` for a portfolio. Empty for the outside group: its heading is
  // copy, so the caller shows the translated one when `isOutside`.
  label: string;
  isOutside: boolean;
  // The person is not an admin of this portfolio.
  isNotAdmin: boolean;
  // This portfolio's pages were read only in part.
  isPartial: boolean;
  // The pages the search keeps, disabled ones included.
  profiles: SocialProfile[];
  // The ids of those pages that may be ticked, as the selection hook knows them.
  connectableIds: string[];
};

type Group = { label: string; isOutside: boolean; profiles: SocialProfile[] };

// Same rule as the one-list picker: a personal page is outside every
// portfolio. So is a page that carries no portfolio at all, which is what
// "not in any of your portfolios" says.
const isOutsidePage = (profile: SocialProfile) =>
  profile.relation === 'personal' || !profile.businessId;

const isConnectable = (profile: SocialProfile) => !profile.isDisabled;

const groupByPortfolio = (profiles: SocialProfile[]): Map<string, Group> => {
  const groups = new Map<string, Group>();

  profiles.forEach((profile) => {
    const outside = isOutsidePage(profile);
    // Outside pages share one group; the others group by their route portfolio.
    const key = outside ? OUTSIDE_GROUP_KEY : (profile.businessId as string);
    const group = groups.get(key);

    if (group) {
      group.profiles.push(profile);
    } else {
      groups.set(key, {
        // A portfolio with no name is shown (and sorted) by its id.
        label: outside ? '' : profile.businessName || key,
        isOutside: outside,
        profiles: [profile],
      });
    }
  });

  return groups;
};

// What a search leaves of one group: all of it when the portfolio's label
// matches, else only the pages whose name matches.
const filterGroup = (group: Group, search: string) => {
  if (!search || group.label.toLowerCase().includes(search)) {
    return group.profiles;
  }

  return group.profiles.filter((profile) =>
    profile.name.toLowerCase().includes(search),
  );
};

// social-login keeps a portfolio whose pages could not be read in full even
// when it lists none of them, so the person sees which one is short. It gets
// an empty column; a search keeps it only when its label matches.
const addEmptyPartialPortfolios = (
  groups: Map<string, Group>,
  portfolios: PortfolioMarkers[],
) => {
  portfolios.forEach((portfolio) => {
    if (portfolio.partial && !groups.has(portfolio.id)) {
      groups.set(portfolio.id, {
        label: portfolio.name || portfolio.id,
        isOutside: false,
        profiles: [],
      });
    }
  });

  return groups;
};

// Portfolios by label, then the outside group. `search` is matched
// case-insensitively against a portfolio's label and its pages' names; a
// portfolio left with no page is dropped (except an empty partial one whose
// label matches), and `connectableIds` follow what the search keeps, so a
// select-all acts on the visible pages.
export const buildPortfolioColumns = (
  profiles: SocialProfile[],
  portfolios: PortfolioMarkers[],
  search: string,
): PortfolioColumn[] => {
  const needle = search.trim().toLowerCase();
  const markersById = new Map(
    portfolios.map((portfolio) => [portfolio.id, portfolio]),
  );
  const columns: PortfolioColumn[] = [];

  const groups = addEmptyPartialPortfolios(
    groupByPortfolio(profiles),
    portfolios,
  );

  groups.forEach((group, key) => {
    const kept = filterGroup(group, needle);
    const isEmptyAndListed =
      group.profiles.length === 0 &&
      (!needle || group.label.toLowerCase().includes(needle));

    if (kept.length === 0 && !isEmptyAndListed) {
      return;
    }

    const marker = group.isOutside ? undefined : markersById.get(key);

    columns.push({
      key,
      label: group.label,
      isOutside: group.isOutside,
      // `null` means its roles are unknown, which is not marked.
      isNotAdmin: marker?.is_admin === false,
      isPartial: !!marker?.partial,
      profiles: kept,
      connectableIds: kept
        .filter(isConnectable)
        .map((profile) => String(profile.id)),
    });
  });

  return columns.sort(
    (a, b) =>
      Number(a.isOutside) - Number(b.isOutside) ||
      a.label.localeCompare(b.label),
  );
};

// The `selected/connectable` badge of a portfolio row, or `null` when none of
// its pages can be ticked (it shows no count). Selected pages that are
// disabled or in another column do not count.
export const countLabel = (
  column: PortfolioColumn,
  selectedIds: ReadonlySet<string> | readonly string[],
): { selected: number; connectable: number } | null => {
  if (column.connectableIds.length === 0) {
    return null;
  }

  const selected: ReadonlySet<string> =
    selectedIds instanceof Set ? selectedIds : new Set(selectedIds);

  return {
    selected: column.connectableIds.filter((id) => selected.has(id)).length,
    connectable: column.connectableIds.length,
  };
};

// The first portfolio with a page to tick, else the first column.
export const firstOpenKey = (columns: PortfolioColumn[]): string | null =>
  (columns.find((column) => column.connectableIds.length > 0) ?? columns[0])
    ?.key ?? null;

// Keeps the open portfolio while it is listed; when a search filters it out,
// opens the first one that is left (null when the search left none).
export const resolveOpenKey = (
  columns: PortfolioColumn[],
  currentKey: string | null,
): string | null =>
  currentKey !== null && columns.some((column) => column.key === currentKey)
    ? currentKey
    : firstOpenKey(columns);
